import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import Button from 'ext-ui/components/Button/Button'
import moment, { Moment } from 'moment-timezone'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FocusedInputShape, isInclusivelyBeforeDay } from 'react-dates'
import { useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import CalendarDayPickerRange from 'src/components/CalendarDayPickerRange/CalendarDayPickerRange'
import DateTextInputField from 'src/components/DateTextInputField/DateTextInputField'
import DividerLineDiv from 'src/components/DividerLineDiv/DividerLineDiv'
import HoverTooltip from 'src/components/HoverTooltip/HoverTooltip'
import SvgChevronDown from 'src/components/Svgs/SvgChevronDown'
import SvgChevronUp from 'src/components/Svgs/SvgChevronUp'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import { useDropdownOpen } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { accountsUrl, expensesTransactionsUrl } from 'src/navigation/urls'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { css } from 'styled-components'

interface DatesPickerDropdownProps {
  since: Moment | undefined
  until: Moment | undefined
  setSince: (val: Moment | undefined) => void
  setUntil: (val: Moment | undefined) => void
  onProcessDatesChange?: (
    since: Moment | undefined,
    until: Moment | undefined,
  ) => { since: Moment | undefined; until: Moment | undefined }
  onApplyDates?: (since: Moment | undefined, until: Moment | undefined) => void
  onDropdownOpen?: () => void
  onDropdownClose?: () => void
  sinceLabel?: React.ReactNode
  untilLabel?: React.ReactNode
  sinceTooltipText?: React.ReactNode
  untilTooltipText?: React.ReactElement
  disabled?: boolean | 'since' | 'until'
  loading?: boolean
  initialMonth?: Moment
  isOutsideRange?: (day: moment.Moment) => boolean
  errorMessage?: string | React.ReactElement
  footerContent?: React.ReactElement
  minimumRangeSize?: number
  utc?: boolean
  keepDropdownOpen?: boolean
  startAndKeepOpen?: boolean
  buttonMode?: boolean
  hideLabel?: boolean
  hideShadow?: boolean
}

const DatesPickerDropdown: React.FC<DatesPickerDropdownProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const locale = useSelector(selectNullSafeLocaleFromState)

  const { analytics } = useAnalyticsContext()

  const { dropdownOpen, divRef, handleOpenerClick } = useDropdownOpen({
    closeOnSecondClick: true,
    closeOnAnyClick: true,
    keepDropdownOpen: props.keepDropdownOpen,
    startAndKeepOpen: props.startAndKeepOpen,
  })

  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [calendarStartDate, setCalendarStartDate] = useState<string>('')
  const [calendarEndDate, setCalendarEndDate] = useState<string>('')

  const disabled = useMemo((): boolean | 'startDate' | 'endDate' => {
    if (props.disabled === true) return true
    if (props.disabled === 'since') return 'startDate'
    if (props.disabled === 'until') return 'endDate'
    return false
  }, [props.disabled])

  const initialFocusInput = useMemo((): FocusedInputShape | null => {
    if (props.disabled === true) return null
    else if (props.disabled === 'since') return 'endDate'
    else return 'startDate'
  }, [props.disabled])

  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(
    null,
  )
  const [hover, setHover] = useState<boolean>(false)

  const momentLocal = props.utc ? moment.utc : moment

  const isOutsideRange = useCallback(
    (day: moment.Moment): boolean => {
      if (props.loading === true || props.disabled === true) {
        return true
      } else if (props.isOutsideRange) {
        return props.isOutsideRange(day)
      } else {
        return !isInclusivelyBeforeDay(day, momentLocal())
      }
    },
    [momentLocal, props.disabled, props.isOutsideRange, props.loading],
  )

  const handleStartTextInputChange = useCallback(
    (date: string): void => {
      setStartDate(date)
      const parsedDate = momentLocal(date, 'll', true).locale(locale)
      if (parsedDate.isValid()) {
        setCalendarStartDate(parsedDate.format('ll'))
        if (!props.onApplyDates) {
          props.setSince(parsedDate.clone())
        }
      }
    },
    [locale, momentLocal, props.onApplyDates, props.setSince],
  )

  const handleEndTextInputChange = useCallback(
    (date: string): void => {
      setEndDate(date)
      const parsedDate = momentLocal(date, 'll', true).locale(locale)
      if (parsedDate.isValid()) {
        setCalendarEndDate(parsedDate.format('ll'))
        if (!props.onApplyDates) {
          props.setUntil(parsedDate)
        }
      }
    },
    [locale, momentLocal, props.onApplyDates, props.setUntil],
  )

  const onDatesChange = useCallback(
    (arg: { startDate: Moment | null; endDate: Moment | null }): void => {
      let { startDate: start, endDate: end } = arg

      if (props.onProcessDatesChange) {
        const processed = props.onProcessDatesChange(
          start ?? undefined,
          end ?? undefined,
        )
        start = processed.since ?? null
        end = processed.until ?? null
      }

      const startFormatted = start?.locale(locale).format('ll') || ''
      const endFormatted = end?.locale(locale).format('ll') || ''

      setStartDate(startFormatted)
      setEndDate(endFormatted)
      setCalendarStartDate(startFormatted)
      setCalendarEndDate(endFormatted)

      if (!props.onApplyDates && focusedInput === 'endDate' && start && end) {
        if (!props.since || !start.isSame(props.since, 'day')) {
          props.setSince(start.clone())
        }
        props.setUntil(end.clone())
      }
    },
    [
      focusedInput,
      locale,
      props.onApplyDates,
      props.onProcessDatesChange,
      props.since,
      props.setSince,
      props.setUntil,
    ],
  )

  const onFocusChange = useCallback(
    (focusedInput: FocusedInputShape | null): void => {
      if (!disabled) setFocusedInput(focusedInput)
    },
    [disabled],
  )

  const calendarMonthKey = useMemo((): string => {
    const monthSource = calendarStartDate || calendarEndDate
    if (!monthSource) return 'calendar-empty'
    const parsedDate = momentLocal(monthSource, 'll', true)
    return parsedDate.isValid()
      ? parsedDate.format('YYYY-MM')
      : 'calendar-invalid'
  }, [calendarEndDate, calendarStartDate, momentLocal])

  const handlePageLocation = (): string => {
    return matchPath(
      calculatedBackgroundLocation.pathname,
      expensesTransactionsUrl(),
    )
      ? 'expenses transactions'
      : matchPath(calculatedBackgroundLocation.pathname, accountsUrl())
        ? 'account transactions'
        : ''
  }

  const handleSegmentAnalyticsOnDropdownOpen = (): void => {
    if (!dropdownOpen) {
      analytics?.track(
        handlePageLocation() + ' filter - open date picker - dropdown press',
        {
          screen: calculatedBackgroundLocation.pathname,
          interaction: 'dropdown press',
          intent: 'open date picker',
        },
      )
    }
  }

  useEffect((): void => {
    if (dropdownOpen) {
      const startFormatted = props.since?.format('ll') || ''
      const endFormatted = props.until?.format('ll') || ''
      setStartDate(startFormatted)
      setEndDate(endFormatted)
      setCalendarStartDate(startFormatted)
      setCalendarEndDate(endFormatted)
    }
  }, [props.since, props.until, dropdownOpen])

  useEffect((): void => {
    !dropdownOpen && setFocusedInput(initialFocusInput)
  }, [dropdownOpen, initialFocusInput])

  useEffect(() => {
    if (dropdownOpen) {
      props.onDropdownOpen?.()
    } else {
      props.onDropdownClose?.()
    }
  }, [dropdownOpen])

  const dateText = (
    <>
      {props.since !== undefined
        ? `${props.since.format('ll')} - ${props.until?.format('ll') || ''}`
        : translate('On or before ${date}', {
            date: `${props.until?.format('ll')}`,
          })}
    </>
  )

  return (
    <div
      data-testid={'datePickerDropdown'}
      css={css`
        position: relative;
      `}
    >
      {!props.hideLabel &&
        (!props.buttonMode ? (
          <span
            ref={divRef}
            onClick={(e): void => {
              if (disabled !== true) {
                e.stopPropagation()
                handleOpenerClick(e)
                handleSegmentAnalyticsOnDropdownOpen()
              }
            }}
            onKeyPress={(e): void => {
              if (disabled !== true) {
                e.stopPropagation()
                handleOpenerClick(e)
              }
            }}
            role="button"
            tabIndex={0}
            css={css`
              display: flex;
              align-items: center;
              margin-top: 5px;
              cursor: ${disabled === true ? 'default' : 'pointer'};
              ${getFontCss(
                14,
                disabled === true
                  ? styleConstants.colors.gray_darkest
                  : themeConstants.colors.cta,
                styleConstants.fontFamilies.regular,
              )};
              height: 18px;
            `}
          >
            {dateText}
            {disabled !== true && (
              <>
                {!dropdownOpen ? (
                  <SvgChevronDown
                    css={css`
                      transform: scale(calc(20 / 24)) translateX(4px);
                    `}
                  />
                ) : (
                  <SvgChevronUp
                    css={css`
                      transform: scale(calc(20 / 24)) translateX(4px);
                    `}
                  />
                )}
              </>
            )}
          </span>
        ) : (
          <span
            css={css`
              display: flex;
              align-items: center;
              ${getFontCss(
                16,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            {dateText}
            <div
              css={css`
                position: relative;
                margin-left: 6px;
              `}
            >
              <SvgUpdate
                className={'svg-update'}
                css={css`
                  transform: scale(calc(16 / 24));
                  margin-top: 5px;
                  cursor: pointer;
                `}
                onMouseEnter={(): void => setHover(true)}
                onMouseLeave={(): void => setHover(false)}
                onClick={(e): void => {
                  e.stopPropagation()
                  handleOpenerClick(e)
                  handleSegmentAnalyticsOnDropdownOpen()
                }}
              />
              <HoverTooltip
                css={css`
                  margin-top: -8px;
                  margin-left: 8px;
                `}
                show={hover}
                tooltipText={translate('Edit End Date of Current Statement')}
              />
            </div>
          </span>
        ))}
      {dropdownOpen && (
        <div
          onClick={(e): void => {
            e.stopPropagation()
          }}
          onKeyPress={(): void => {
            undefined
          }}
          role="button"
          tabIndex={0}
          css={css`
            display: flex;
            flex-direction: column;
            position: absolute;
            left: ${props.buttonMode ? '-95px' : '0px'};
            top: ${props.hideShadow ? '0px' : '22px'};
            z-index: 2;
            padding-top: ${props.hideShadow ? '0px' : '10px'};

            ${getBreakpointDown(breakpointSizes.XXSM)(css`
              left: -30px;
            `)}
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              width: 368px;
              border-radius: 10px;
              box-shadow: ${props.hideShadow
                ? 'none'
                : '5px 4px 10px 5px rgba(0, 0, 0, 0.1)'};
              background-color: ${styleConstants.colors.white};
              padding-top: 20px;
              padding-bottom: 20px;
              > * {
                margin-left: 20px;
                margin-right: 20px;
              }
            `}
          >
            <div
              css={css`
                display: flex;
                justify-content: space-between;
                margin-bottom: 12px;
              `}
            >
              <DateTextInputField
                label={props.sinceLabel ?? translate('Start')}
                placeholder={''}
                value={startDate}
                focus={focusedInput === 'startDate' || focusedInput === null}
                onClick={(): void => {
                  focusedInput === 'endDate' && setFocusedInput('startDate')
                }}
                onChange={(val: string): void => {
                  setStartDate(val)
                  const parsedDate = momentLocal(val, 'll', true).locale(locale)
                  if (parsedDate.isValid()) {
                    setCalendarStartDate(parsedDate.format('ll'))
                  }
                }}
                onBlur={(val: string): void => handleStartTextInputChange(val)}
                disabled={
                  props.loading === true ||
                  disabled === true ||
                  disabled === 'startDate'
                }
                tooltipText={props.sinceTooltipText}
              />
              <div
                css={css`
                  display: flex;
                  align-self: flex-end;
                  margin-bottom: 12px;
                  ${getFontCss(
                    14,
                    styleConstants.colors.gray_darkest,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {'-'}
              </div>
              <DateTextInputField
                label={props.untilLabel ?? translate('End')}
                placeholder={''}
                value={endDate}
                focus={focusedInput === 'endDate'}
                onClick={(): void => {
                  if (focusedInput === 'startDate' || focusedInput === null) {
                    setFocusedInput('endDate')
                  }
                }}
                onChange={(val: string): void => {
                  setEndDate(val)
                  const parsedDate = momentLocal(val, 'll', true).locale(locale)
                  if (parsedDate.isValid()) {
                    setCalendarEndDate(parsedDate.format('ll'))
                  }
                }}
                onBlur={(val: string): void => handleEndTextInputChange(val)}
                disabled={
                  props.loading === true ||
                  disabled === true ||
                  disabled === 'endDate'
                }
                tooltipText={props.untilTooltipText}
              />
            </div>
            <CalendarDayPickerRange
              key={calendarMonthKey}
              isOutsideRange={isOutsideRange}
              onDatesChange={onDatesChange}
              onFocusChange={onFocusChange}
              focusedInput={focusedInput}
              startDate={calendarStartDate}
              endDate={calendarEndDate}
              momentLocal={momentLocal}
            />
            {props.onApplyDates && (
              <div
                css={css`
                  display: flex;
                  gap: 8px;
                  margin-top: 20px;
                `}
              >
                <Button
                  disabled={
                    momentLocal(startDate, 'll').isSame(props.since, 'day') &&
                    momentLocal(endDate, 'll').isSame(props.until, 'day')
                  }
                  loading={props.loading}
                  type="submit"
                  variant="primary"
                  testId={'datepickerApplyBtn'}
                  onClick={(): void =>
                    props.onApplyDates?.(
                      momentLocal(startDate, 'll'),
                      momentLocal(endDate, 'll'),
                    )
                  }
                >
                  {translate('Apply')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  testId={'datepickerCancelBtn'}
                  onClick={handleOpenerClick}
                >
                  {translate('Cancel')}
                </Button>
              </div>
            )}
            {props.errorMessage && (
              <div
                css={css`
                  margin-top: 20px;
                  ${getFontCss(
                    14,
                    styleConstants.colors.red,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {props.errorMessage}
              </div>
            )}
            {props.footerContent && (
              <>
                <DividerLineDiv
                  css={css`
                    margin: 20px 0 0 0;
                  `}
                />
                <div
                  css={css`
                    padding-top: 20px;
                  `}
                >
                  {props.footerContent}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default DatesPickerDropdown
